import axios from "axios";
import { tokenManager } from "../utils/tokenManager";

/**
 * ---------------------------------------------------
 * AXIOS INSTANCE
 * ---------------------------------------------------
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true, // 🔑 send refresh-token cookie
});

/**
 * ---------------------------------------------------
 * TOKEN REFRESH STATE
 * ---------------------------------------------------
 */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

/**
 * ---------------------------------------------------
 * REQUEST INTERCEPTOR - Attach Authorization Header
 * ---------------------------------------------------
 */
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * ---------------------------------------------------
 * RESPONSE INTERCEPTOR - Handle 401 & Token Refresh
 * ---------------------------------------------------
 */
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // ❌ No response (network error)
    if (!error.response) {
      return Promise.reject(error);
    }

    // ❌ Not an auth error
    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    // ❌ Prevent infinite retry
    if (originalRequest._retry) {
      tokenManager.clearToken();
      return Promise.reject(error);
    }

    // 🔁 If refresh already in progress → queue request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // 🔄 REFRESH TOKEN
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}api/auth/refresh-token`,
        {},
        { withCredentials: true }
      );

      const newAccessToken = data?.accessToken;

      if (!newAccessToken) {
        throw new Error("No access token returned");
      }

      // ✅ Save token
      tokenManager.setToken(newAccessToken);

      // ✅ Resolve queued requests
      processQueue(null, newAccessToken);

      // 🔁 Retry original request
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);

    } catch (refreshError) {
      // ❌ Refresh failed → logout
      processQueue(refreshError, null);
      tokenManager.clearToken();

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
